import { useRef } from 'react'
import { useLocation, useOutlet, Outlet } from 'react-router-dom'
import { AnimatePresence, motion } from 'framer-motion'

const pageVariants = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit:    { opacity: 0, y: -6 },
}

const pageTransition = { duration: 0.22, ease: 'easeInOut' as const }

export default function AnimatedOutlet() {
  const location = useLocation()
  const prevKey = useRef(location.pathname)
  const key = location.pathname.split('/').slice(0, 3).join('/')
  // The element, not `<Outlet />`: AnimatePresence keeps the leaving page
  // mounted while it fades out, and an `<Outlet />` inside it reads the NEW
  // location — so the incoming page rendered in the leaving wrapper, faded out,
  // unmounted, and mounted a second time. Every navigation showed its page
  // twice. This element carries the matches it was rendered with.
  const outlet = useOutlet()

  // Disable animations for settings routes since SettingsLayout has its own layout
  const isSettingsRoute = location.pathname.startsWith('/settings')

  if (isSettingsRoute) {
    return (
      <div className="flex-1 sm:p-4 md:p-2 xl:p-4 flex h-full overflow-hidden">
        <Outlet />
      </div>
    )
  }

  return (
    <AnimatePresence mode="wait" initial={false}>
      <motion.div
        key={key}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition={pageTransition}
        className="flex-1 flex h-full overflow-hidden"
        onAnimationComplete={() => { prevKey.current = location.pathname }}
      >
        {outlet}
      </motion.div>
    </AnimatePresence>
  )
}
